import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Bus as BusIcon, GraduationCap as GraduationCapIcon, Landmark as LandmarkIcon, MessageCircle as MessageCircleIcon, Check as CheckIcon, ArrowLeft as ArrowLeftIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { LanguageProvider } from '../context/LanguageContext';
export function SikhHeritageForwardPage() {
  useEffect(() => {
    document.body.classList.add('bg-[#fefaf3]', 'text-[#0f1729]', 'overflow-x-hidden');
    return () => {
      document.body.classList.remove('bg-[#fefaf3]', 'text-[#0f1729]', 'overflow-x-hidden');
    };
  }, []);
  const fadeInUp = {
    initial: {
      opacity: 0,
      y: 30
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.8
    }
  };
  return <LanguageProvider data-id="element-6522">
      <div className="relative min-h-screen bg-[#fefaf3] font-sans" data-id="element-6523">
        <Navbar data-id="element-6524" />

        {/* 1. Hero Section */}
        <section className="relative min-h-[90vh] bg-[#0a0f1f] flex flex-col items-center justify-center overflow-hidden pt-20 pb-16" data-id="element-6525">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#c89b3c]/15 via-[#0a0f1f] to-[#0a0f1f]" data-id="element-6526"></div>

          <div className="relative z-10 container mx-auto px-6 text-center max-w-4xl" data-id="element-6527">
            <motion.p {...fadeInUp} className="text-[#c89b3c] tracking-[0.3em] text-[10px] md:text-xs uppercase mb-6 font-medium" data-id="element-6528">
              A Program of The Kingdoms of Punjab
            </motion.p>

            <motion.h1 {...fadeInUp} transition={{
            duration: 0.8,
            delay: 0.1
          }} className="font-serif text-5xl md:text-7xl lg:text-8xl text-[#fefaf3] mb-6 tracking-tight leading-[1.1]" data-id="element-6529">
              Sikh Heritage Forward
            </motion.h1>

            <motion.p {...fadeInUp} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="text-lg md:text-2xl text-[#fefaf3]/70 font-light mb-8 max-w-3xl mx-auto" data-id="element-6530">
              A pay-it-forward student access program for The Kingdoms of Punjab
            </motion.p>

            <motion.p {...fadeInUp} transition={{
            duration: 0.8,
            delay: 0.3
          }} className="text-[#fefaf3]/50 font-light leading-relaxed mb-12 max-w-2xl mx-auto text-sm md:text-base" data-id="element-6531">
              Sponsors fund student bus access during Sikh Heritage Month and
              beyond, supporting both student access AND the creation and
              continuity of this immersive cultural project.
            </motion.p>

            <motion.div {...fadeInUp} transition={{
            duration: 0.8,
            delay: 0.4
          }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-6532">
              <a href="https://wa.me/14169998778?text=I%20would%20like%20to%20sponsor%20a%20bus%20for%20Sikh%20Heritage%20Forward" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#c89b3c] hover:bg-[#b8860b] text-[#0a0f1f] px-8 py-4 text-xs md:text-sm tracking-[0.15em] uppercase font-medium transition-colors duration-300" data-id="element-6533">
                Sponsor a Bus
              </a>
              <a href="#tiers" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-[#c89b3c]/50 hover:border-[#c89b3c] text-[#fefaf3] px-8 py-4 text-xs md:text-sm tracking-[0.15em] uppercase font-medium transition-colors duration-300" data-id="element-6534">
                View Sponsorship Tiers
              </a>
            </motion.div>

            <motion.p {...fadeInUp} transition={{
            duration: 0.8,
            delay: 0.5
          }} className="mt-12 text-[#c89b3c]/40 text-xs font-light italic" data-id="element-6535">
              In support of Sikh Heritage Month and beyond
            </motion.p>
          </div>
        </section>

        {/* 2. Why It Matters Section */}
        <section className="py-24 md:py-32 bg-[#fefaf3]" data-id="element-6536">
          <div className="container mx-auto px-6 max-w-4xl" data-id="element-6537">
            <motion.div {...fadeInUp} className="text-center mb-16" data-id="element-6538">
              <p className="text-[#c89b3c] tracking-[0.2em] text-xs uppercase mb-4 font-semibold" data-id="element-6539">
                Why It Matters
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#0f1729] mb-8" data-id="element-6540">
                Heritage is not preserved by accident.
              </h2>
              <div className="w-16 h-px bg-[#c89b3c]/30 mx-auto" data-id="element-6541"></div>
            </motion.div>

            <motion.div {...fadeInUp} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="prose prose-lg mx-auto text-[#0f1729]/70 font-light leading-relaxed" data-id="element-6542">
              <p className="mb-6" data-id="element-6543">
                Many young Sikhs grow up disconnected from the depth of their
                own heritage. They learn fragments of history, but rarely get to
                step inside it, feel it, and understand the magnitude of what
                their ancestors built.
              </p>
              <p className="mb-6" data-id="element-6544">
                The Kingdoms of Punjab is a once-in-a-generation immersive
                cultural experience designed to change that. But without
                sponsorship, students from public schools, Gurdwara youth
                groups, and diaspora communities cannot easily access it.
              </p>
              <p className="mb-10" data-id="element-6545">
                Sponsors are the bridge. By funding student access, you are not
                just buying a ticket—you are making heritage education
                accessible while securing the project's future as a permanent
                cultural institution.
              </p>

              <blockquote className="border-l-2 border-[#c89b3c] pl-6 py-2 my-12" data-id="element-6546">
                <p className="font-serif text-2xl md:text-3xl text-[#c89b3c] italic leading-snug" data-id="element-6547">
                  "When a young Sikh sees their history alive—not in a textbook,
                  but all around them—something awakens. That awakening is what
                  we are funding."
                </p>
              </blockquote>
            </motion.div>
          </div>
        </section>

        {/* 3. What Your Sponsorship Funds */}
        <section className="py-24 md:py-32 bg-[#f9f4ec]" data-id="element-6548">
          <div className="container mx-auto px-6 max-w-6xl" data-id="element-6549">
            <motion.div {...fadeInUp} className="text-center mb-16" data-id="element-6550">
              <p className="text-[#c89b3c] tracking-[0.2em] text-xs uppercase mb-4 font-semibold" data-id="element-6551">
                Where The Funds Go
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#0f1729] mb-8" data-id="element-6552">
                Three pillars of impact.
              </h2>
              <div className="w-16 h-px bg-[#c89b3c]/30 mx-auto" data-id="element-6553"></div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-6554">
              <motion.div {...fadeInUp} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="bg-[#fefaf3] border border-[#c89b3c]/20 p-10 text-center hover:border-[#c89b3c]/50 transition-colors duration-300" data-id="element-6555">
                <GraduationCapIcon className="w-10 h-10 text-[#c89b3c] mx-auto mb-6" data-id="element-6556" />
                <h3 className="font-serif text-2xl text-[#0f1729] mb-4" data-id="element-6557">
                  Student Access
                </h3>
                <p className="text-[#0f1729]/60 font-light leading-relaxed text-sm" data-id="element-6558">
                  Funds free or subsidized tickets for schools, Gurdwara youth
                  programs, and community groups to experience the immersive
                  exhibition.
                </p>
              </motion.div>

              <motion.div {...fadeInUp} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="bg-[#fefaf3] border border-[#c89b3c]/20 p-10 text-center hover:border-[#c89b3c]/50 transition-colors duration-300" data-id="element-6559">
                <BusIcon className="w-10 h-10 text-[#c89b3c] mx-auto mb-6" data-id="element-6560" />
                <h3 className="font-serif text-2xl text-[#0f1729] mb-4" data-id="element-6561">
                  Transportation
                </h3>
                <p className="text-[#0f1729]/60 font-light leading-relaxed text-sm" data-id="element-6562">
                  Covers school buses bringing students from across the GTA to
                  the Illuminarium experience, removing the biggest logistical
                  barrier.
                </p>
              </motion.div>

              <motion.div {...fadeInUp} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="bg-[#fefaf3] border border-[#c89b3c]/20 p-10 text-center hover:border-[#c89b3c]/50 transition-colors duration-300" data-id="element-6563">
                <LandmarkIcon className="w-10 h-10 text-[#c89b3c] mx-auto mb-6" data-id="element-6564" />
                <h3 className="font-serif text-2xl text-[#0f1729] mb-4" data-id="element-6565">
                  Project Continuity
                </h3>
                <p className="text-[#0f1729]/60 font-light leading-relaxed text-sm" data-id="element-6566">
                  Supports ongoing development, programming, and the long-term
                  survival of The Kingdoms of Punjab as a permanent cultural
                  institution.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 4. Sponsorship Tiers */}
        <section id="tiers" className="py-24 md:py-32 bg-[#fefaf3]" data-id="element-6567">
          <div className="container mx-auto px-6 max-w-7xl" data-id="element-6568">
            <motion.div {...fadeInUp} className="text-center mb-16" data-id="element-6569">
              <p className="text-[#c89b3c] tracking-[0.2em] text-xs uppercase mb-4 font-semibold" data-id="element-6570">
                Sponsorship Tiers
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#0f1729] mb-6" data-id="element-6571">
                Choose your level of patronage.
              </h2>
              <p className="text-[#0f1729]/60 font-light max-w-2xl mx-auto" data-id="element-6572">
                Every tier is meaningful and recognized. Your contribution
                directly funds student access and preserves our cultural legacy.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-6573">
              {/* Tier 1 */}
              <motion.div {...fadeInUp} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="bg-[#fefaf3] border border-[#c89b3c]/20 p-8 flex flex-col hover:border-[#c89b3c]/40 transition-colors" data-id="element-6574">
                <h3 className="font-serif text-2xl text-[#0f1729] mb-2" data-id="element-6575">
                  Bus Sponsor
                </h3>
                <div className="text-3xl text-[#c89b3c] font-medium mb-6" data-id="element-6576">
                  $10,000
                </div>
                <ul className="space-y-4 mb-8 flex-1" data-id="element-6577">
                  <li className="flex items-start gap-3 text-sm text-[#0f1729]/70 font-light" data-id="element-6578">
                    <CheckIcon className="w-4 h-4 text-[#c89b3c] mt-0.5 flex-shrink-0" data-id="element-6579" />
                    <span data-id="element-6580">Funds: 1 full bus (~50 students)</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-[#0f1729]/70 font-light" data-id="element-6581">
                    <CheckIcon className="w-4 h-4 text-[#c89b3c] mt-0.5 flex-shrink-0" data-id="element-6582" />
                    <span data-id="element-6583">
                      Recognition: Listed as a Bus Sponsor in program materials
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-[#0f1729]/70 font-light" data-id="element-6584">
                    <CheckIcon className="w-4 h-4 text-[#c89b3c] mt-0.5 flex-shrink-0" data-id="element-6585" />
                    <span data-id="element-6586">
                      Includes: 2 complimentary VIP tickets to opening
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-[#0f1729]/70 font-light" data-id="element-6587">
                    <CheckIcon className="w-4 h-4 text-[#c89b3c] mt-0.5 flex-shrink-0" data-id="element-6588" />
                    <span data-id="element-6589">Tax receipt provided</span>
                  </li>
                </ul>
                <a href="https://wa.me/14169998778?text=I%20would%20like%20to%20become%20a%20Bus%20Sponsor%20for%20Sikh%20Heritage%20Forward" target="_blank" rel="noopener noreferrer" className="block w-full text-center border border-[#c89b3c] text-[#c89b3c] hover:bg-[#c89b3c] hover:text-[#fefaf3] py-3 text-xs tracking-[0.15em] uppercase font-medium transition-colors" data-id="element-6590">
                  Become a Bus Sponsor
                </a>
              </motion.div>

              {/* Tier 2 */}
              <motion.div {...fadeInUp} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="bg-[#fefaf3] border border-[#c89b3c]/20 p-8 flex flex-col hover:border-[#c89b3c]/40 transition-colors" data-id="element-6591">
                <h3 className="font-serif text-2xl text-[#0f1729] mb-2" data-id="element-6592">
                  Heritage Sponsor
                </h3>
                <div className="text-3xl text-[#c89b3c] font-medium mb-6" data-id="element-6593">
                  $25,000
                </div>
                <ul className="space-y-4 mb-8 flex-1" data-id="element-6594">
                  <li className="flex items-start gap-3 text-sm text-[#0f1729]/70 font-light" data-id="element-6595">
                    <CheckIcon className="w-4 h-4 text-[#c89b3c] mt-0.5 flex-shrink-0" data-id="element-6596" />
                    <span data-id="element-6597">Funds: 3 buses (~150 students) over the season</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-[#0f1729]/70 font-light" data-id="element-6598">
                    <CheckIcon className="w-4 h-4 text-[#c89b3c] mt-0.5 flex-shrink-0" data-id="element-6599" />
                    <span data-id="element-6600">
                      Recognition: Named on the Heritage Wall + program credits
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-[#0f1729]/70 font-light" data-id="element-6601">
                    <CheckIcon className="w-4 h-4 text-[#c89b3c] mt-0.5 flex-shrink-0" data-id="element-6602" />
                    <span data-id="element-6603">
                      Includes: 4 VIP tickets + private behind-the-scenes tour
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-[#0f1729]/70 font-light" data-id="element-6604">
                    <CheckIcon className="w-4 h-4 text-[#c89b3c] mt-0.5 flex-shrink-0" data-id="element-6605" />
                    <span data-id="element-6606">Hosted recognition dinner</span>
                  </li>
                </ul>
                <a href="https://wa.me/14169998778?text=I%20would%20like%20to%20become%20a%20Heritage%20Sponsor%20for%20Sikh%20Heritage%20Forward" target="_blank" rel="noopener noreferrer" className="block w-full text-center border border-[#c89b3c] text-[#c89b3c] hover:bg-[#c89b3c] hover:text-[#fefaf3] py-3 text-xs tracking-[0.15em] uppercase font-medium transition-colors" data-id="element-6607">
                  Become a Heritage Sponsor
                </a>
              </motion.div>

              {/* Tier 3 - Featured */}
              <motion.div {...fadeInUp} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="bg-[#fefaf3] border-2 border-[#c89b3c] p-8 flex flex-col relative shadow-[0_0_30px_rgba(200,155,60,0.15)] lg:-translate-y-4" data-id="element-6608">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#c89b3c] text-[#0a0f1f] px-4 py-1 text-[10px] tracking-[0.2em] uppercase font-bold whitespace-nowrap" data-id="element-6609">
                  Most Popular
                </div>
                <h3 className="font-serif text-2xl text-[#0f1729] mb-2" data-id="element-6610">
                  Legacy Sponsor
                </h3>
                <div className="text-3xl text-[#c89b3c] font-medium mb-6" data-id="element-6611">
                  $50,000
                </div>
                <ul className="space-y-4 mb-8 flex-1" data-id="element-6612">
                  <li className="flex items-start gap-3 text-sm text-[#0f1729]/70 font-light" data-id="element-6613">
                    <CheckIcon className="w-4 h-4 text-[#c89b3c] mt-0.5 flex-shrink-0" data-id="element-6614" />
                    <span data-id="element-6615">
                      Funds: 6 buses (~300 students) + named education program
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-[#0f1729]/70 font-light" data-id="element-6616">
                    <CheckIcon className="w-4 h-4 text-[#c89b3c] mt-0.5 flex-shrink-0" data-id="element-6617" />
                    <span data-id="element-6618">
                      Recognition: Premium placement on Heritage Wall + named
                      scholarship in your honor
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-[#0f1729]/70 font-light" data-id="element-6619">
                    <CheckIcon className="w-4 h-4 text-[#c89b3c] mt-0.5 flex-shrink-0" data-id="element-6620" />
                    <span data-id="element-6621">
                      Includes: Reserved hosted table for 8 + private chef
                      interaction
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-[#0f1729]/70 font-light" data-id="element-6622">
                    <CheckIcon className="w-4 h-4 text-[#c89b3c] mt-0.5 flex-shrink-0" data-id="element-6623" />
                    <span data-id="element-6624">Speaking opportunity at recognition dinner</span>
                  </li>
                </ul>
                <a href="https://wa.me/14169998778?text=I%20would%20like%20to%20become%20a%20Legacy%20Sponsor%20for%20Sikh%20Heritage%20Forward" target="_blank" rel="noopener noreferrer" className="block w-full text-center bg-[#c89b3c] text-[#0a0f1f] hover:bg-[#b8860b] py-3 text-xs tracking-[0.15em] uppercase font-medium transition-colors" data-id="element-6625">
                  Become a Legacy Sponsor
                </a>
              </motion.div>

              {/* Tier 4 */}
              <motion.div {...fadeInUp} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="bg-[#0f1729] border border-[#c89b3c]/30 p-8 flex flex-col" data-id="element-6626">
                <h3 className="font-serif text-2xl text-[#fefaf3] mb-2" data-id="element-6627">
                  Founding Patron
                </h3>
                <div className="text-3xl text-[#c89b3c] font-medium mb-6" data-id="element-6628">
                  $100,000+
                </div>
                <ul className="space-y-4 mb-8 flex-1" data-id="element-6629">
                  <li className="flex items-start gap-3 text-sm text-[#fefaf3]/70 font-light" data-id="element-6630">
                    <CheckIcon className="w-4 h-4 text-[#c89b3c] mt-0.5 flex-shrink-0" data-id="element-6631" />
                    <span data-id="element-6632">
                      Funds: A full Sikh Heritage Month program for an entire
                      school district
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-[#fefaf3]/70 font-light" data-id="element-6633">
                    <CheckIcon className="w-4 h-4 text-[#c89b3c] mt-0.5 flex-shrink-0" data-id="element-6634" />
                    <span data-id="element-6635">
                      Recognition: Founding Patron status — permanent
                      recognition as an architect of the project
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-[#fefaf3]/70 font-light" data-id="element-6636">
                    <CheckIcon className="w-4 h-4 text-[#c89b3c] mt-0.5 flex-shrink-0" data-id="element-6637" />
                    <span data-id="element-6638">
                      Includes: All Legacy benefits + named scene/element within
                      the experience
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-[#fefaf3]/70 font-light" data-id="element-6639">
                    <CheckIcon className="w-4 h-4 text-[#c89b3c] mt-0.5 flex-shrink-0" data-id="element-6640" />
                    <span data-id="element-6641">Annual private reception with the creative team</span>
                  </li>
                </ul>
                <a href="https://wa.me/14169998778?text=I%20would%20like%20to%20become%20a%20Founding%20Patron%20for%20Sikh%20Heritage%20Forward" target="_blank" rel="noopener noreferrer" className="block w-full text-center border border-[#c89b3c] text-[#c89b3c] hover:bg-[#c89b3c] hover:text-[#0f1729] py-3 text-xs tracking-[0.15em] uppercase font-medium transition-colors" data-id="element-6642">
                  Become a Founding Patron
                </a>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 5. Impact Section */}
        <section className="py-24 md:py-32 bg-[#0a0f1f] relative overflow-hidden" data-id="element-6643">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_var(--tw-gradient-stops))] from-[#c89b3c]/10 via-transparent to-transparent" data-id="element-6644"></div>
          <div className="container mx-auto px-6 relative z-10 max-w-5xl text-center" data-id="element-6645">
            <motion.p {...fadeInUp} className="text-[#c89b3c] tracking-[0.2em] text-xs uppercase mb-4 font-semibold" data-id="element-6646">
              Impact
            </motion.p>
            <motion.h2 {...fadeInUp} transition={{
            delay: 0.1
          }} className="font-serif text-4xl md:text-5xl text-[#fefaf3] mb-16" data-id="element-6647">
              Every bus changes a generation.
            </motion.h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12" data-id="element-6648">
              <motion.div {...fadeInUp} transition={{
              delay: 0.2
            }} data-id="element-6649">
                <div className="font-serif text-5xl md:text-6xl text-[#c89b3c] mb-2" data-id="element-6650">
                  50
                </div>
                <div className="text-[#fefaf3]/60 text-xs md:text-sm font-light uppercase tracking-wider" data-id="element-6651">
                  Students per bus
                </div>
              </motion.div>
              <motion.div {...fadeInUp} transition={{
              delay: 0.3
            }} data-id="element-6652">
                <div className="font-serif text-5xl md:text-6xl text-[#c89b3c] mb-2" data-id="element-6653">
                  12k+
                </div>
                <div className="text-[#fefaf3]/60 text-xs md:text-sm font-light uppercase tracking-wider" data-id="element-6654">
                  Students in Year 1
                </div>
              </motion.div>
              <motion.div {...fadeInUp} transition={{
              delay: 0.4
            }} data-id="element-6655">
                <div className="font-serif text-5xl md:text-6xl text-[#c89b3c] mb-2" data-id="element-6656">
                  90m
                </div>
                <div className="text-[#fefaf3]/60 text-xs md:text-sm font-light uppercase tracking-wider" data-id="element-6657">
                  Of immersive heritage
                </div>
              </motion.div>
              <motion.div {...fadeInUp} transition={{
              delay: 0.5
            }} data-id="element-6658">
                <div className="font-serif text-5xl md:text-6xl text-[#c89b3c] mb-2" data-id="element-6659">
                  ∞
                </div>
                <div className="text-[#fefaf3]/60 text-xs md:text-sm font-light uppercase tracking-wider" data-id="element-6660">
                  Generational ripple
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 6. For Sikh & Punjabi Business Leaders */}
        <section className="py-24 md:py-32 bg-[#fefaf3]" data-id="element-6661">
          <div className="container mx-auto px-6 max-w-6xl" data-id="element-6662">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-6663">
              <motion.div {...fadeInUp} data-id="element-6664">
                <p className="text-[#c89b3c] tracking-[0.2em] text-xs uppercase mb-4 font-semibold" data-id="element-6665">
                  A Call to Our Community
                </p>
                <h2 className="font-serif text-4xl md:text-5xl text-[#0f1729] mb-8 leading-tight" data-id="element-6666">
                  For the leaders who build, the patrons who preserve.
                </h2>
                <div className="space-y-6 text-[#0f1729]/70 font-light leading-relaxed" data-id="element-6667">
                  <p data-id="element-6668">
                    Our community has built businesses, neighborhoods, and
                    institutions across Canada. We have established ourselves as
                    leaders in every industry.
                  </p>
                  <p data-id="element-6669">
                    This is the next institution worth building—a permanent
                    immersive home for our heritage. Sponsorship is not charity;
                    it is legacy. It is the continuation of our deepest values:{' '}
                    <em className="font-serif text-lg" data-id="element-6670">dasvandh</em> (giving a
                    tenth) and <em className="font-serif text-lg" data-id="element-6671">seva</em>{' '}
                    (selfless service).
                  </p>
                  <p data-id="element-6672">
                    Your name on the Heritage Wall stands beside the historical
                    figures the experience honors. You become an architect of
                    the future by preserving the past.
                  </p>
                </div>
                <div className="mt-10" data-id="element-6673">
                  <a href="https://wa.me/14169998778?text=I%20would%20like%20to%20speak%20with%20the%20founders%20about%20Sikh%20Heritage%20Forward" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border-b border-[#c89b3c] text-[#c89b3c] hover:text-[#0f1729] hover:border-[#0f1729] pb-1 text-sm tracking-[0.15em] uppercase font-medium transition-colors" data-id="element-6674">
                    <MessageCircleIcon className="w-4 h-4" data-id="element-6675" />
                    Speak with the Founders
                  </a>
                </div>
              </motion.div>

              <motion.div {...fadeInUp} transition={{
              delay: 0.2
            }} className="bg-[#f9f4ec] border border-[#c89b3c]/20 p-12 text-center" data-id="element-6676">
                <h3 className="font-serif text-3xl text-[#0f1729] mb-10" data-id="element-6677">
                  Our Values
                </h3>
                <div className="space-y-10" data-id="element-6678">
                  <div data-id="element-6679">
                    <div className="text-[#c89b3c] font-serif text-2xl mb-2" data-id="element-6680">
                      Dasvandh
                    </div>
                    <div className="text-[#0f1729]/60 text-sm font-light" data-id="element-6681">
                      Contributing to the community's growth
                    </div>
                  </div>
                  <div data-id="element-6682">
                    <div className="text-[#c89b3c] font-serif text-2xl mb-2" data-id="element-6683">
                      Seva
                    </div>
                    <div className="text-[#0f1729]/60 text-sm font-light" data-id="element-6684">
                      Selfless service for the next generation
                    </div>
                  </div>
                  <div data-id="element-6685">
                    <div className="text-[#c89b3c] font-serif text-2xl mb-2" data-id="element-6686">
                      Vidya
                    </div>
                    <div className="text-[#0f1729]/60 text-sm font-light" data-id="element-6687">
                      The pursuit and sharing of knowledge
                    </div>
                  </div>
                  <div data-id="element-6688">
                    <div className="text-[#c89b3c] font-serif text-2xl mb-2" data-id="element-6689">
                      Virasat
                    </div>
                    <div className="text-[#0f1729]/60 text-sm font-light" data-id="element-6690">
                      Preserving our rich cultural heritage
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 7. Final CTA Section */}
        <section className="py-32 bg-[#0a0f1f] relative overflow-hidden text-center" data-id="element-6691">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-[#c89b3c]/10 via-[#0a0f1f] to-[#0a0f1f]" data-id="element-6692"></div>
          <div className="container mx-auto px-6 relative z-10 max-w-4xl" data-id="element-6693">
            <motion.h2 {...fadeInUp} className="font-serif text-5xl md:text-7xl text-[#fefaf3] mb-2 tracking-tight" data-id="element-6694">
              Sponsor a bus.
            </motion.h2>
            <motion.h2 {...fadeInUp} transition={{
            delay: 0.1
          }} className="font-serif text-5xl md:text-7xl text-[#c89b3c] mb-8 tracking-tight" data-id="element-6695">
              Pay heritage forward.
            </motion.h2>

            <motion.p {...fadeInUp} transition={{
            delay: 0.2
          }} className="text-[#fefaf3]/60 font-light mb-12 max-w-xl mx-auto text-lg" data-id="element-6696">
              Join the visionary leaders ensuring our history is experienced,
              not forgotten.
            </motion.p>

            <motion.div {...fadeInUp} transition={{
            delay: 0.3
          }} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16" data-id="element-6697">
              <a href="https://wa.me/14169998778?text=I%20would%20like%20to%20sponsor%20a%20bus%20for%20Sikh%20Heritage%20Forward" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#c89b3c] hover:bg-[#b8860b] text-[#0a0f1f] px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium transition-colors duration-300" data-id="element-6698">
                Sponsor a Bus
              </a>
              <a href="https://wa.me/14169998778?text=I%20would%20like%20to%20become%20a%20Patron%20for%20Sikh%20Heritage%20Forward" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-[#c89b3c] text-[#c89b3c] hover:bg-[#c89b3c] hover:text-[#0a0f1f] px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium transition-colors duration-300" data-id="element-6699">
                Become a Patron
              </a>
            </motion.div>

            <motion.div {...fadeInUp} transition={{
            delay: 0.4
          }} data-id="element-6700">
              <Link to="/" className="inline-flex items-center gap-2 text-[#fefaf3]/40 hover:text-[#c89b3c] text-xs tracking-[0.15em] uppercase font-light transition-colors" data-id="element-6701">
                <ArrowLeftIcon className="w-3 h-3" data-id="element-6702" />
                Back to Main Site
              </Link>
            </motion.div>
          </div>
        </section>

        <Footer data-id="element-6703" />
      </div>
    </LanguageProvider>;
}